// 🔥 FireStarter - Dashboard Component

import { useEffect, useState } from 'react';
import ResourceValidator from './ResourceValidator';

interface BindingsStatus {
  db: boolean;
  cache: boolean;
  storage: boolean;
  ai: boolean;
}

export function Dashboard() {
  const [bindings, setBindings] = useState<BindingsStatus | null>(null);
  const [health, setHealth] = useState<any>(null);
  const [showValidator, setShowValidator] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, []);

  const apiUrl = import.meta.env.VITE_API_URL || '';

  const fetchStatus = async () => {
    try {
      const healthRes = await fetch(`${apiUrl}/health`);
      const healthData = await healthRes.json();
      setHealth(healthData.data);

      const bindingsRes = await fetch(`${apiUrl}/bindings`);
      const bindingsData = await bindingsRes.json();
      setBindings(bindingsData.data);
    } catch (error) {
      console.error('Failed to fetch status:', error);
    }
  };

  const testAI = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/ai/text`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: 'Say hello!' }),
      });
      const data = await response.json();
      alert(data.success ? data.data.response : 'AI test failed');
    } catch (error) {
      alert('Failed to test AI');
    }
  };

  // Show validator if active
  if (showValidator) {
    return <ResourceValidator onComplete={() => setShowValidator(false)} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-4xl">🔥</span>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {import.meta.env.VITE_PROJECT_NAME || 'FireStarter'}
                </h1>
                <p className="text-sm text-gray-600">
                  {import.meta.env.VITE_PROJECT_DESCRIPTION || 'CloudFlare Full-Stack Template'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                health ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {health ? '● Live' : '○ Offline'}
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* System Status */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">CloudFlare Bindings</h2>
          {bindings ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className={`p-4 rounded-lg ${bindings.db ? 'bg-green-50' : 'bg-gray-50'}`}>
                <div className="text-2xl mb-2">{bindings.db ? '✅' : '⏸️'}</div>
                <div className="font-medium">D1 Database</div>
              </div>
              <div className={`p-4 rounded-lg ${bindings.cache ? 'bg-green-50' : 'bg-gray-50'}`}>
                <div className="text-2xl mb-2">{bindings.cache ? '✅' : '⏸️'}</div>
                <div className="font-medium">KV Cache</div>
              </div>
              <div className={`p-4 rounded-lg ${bindings.storage ? 'bg-green-50' : 'bg-gray-50'}`}>
                <div className="text-2xl mb-2">{bindings.storage ? '✅' : '⏸️'}</div>
                <div className="font-medium">R2 Storage</div>
              </div>
              <div className={`p-4 rounded-lg ${bindings.ai ? 'bg-green-50' : 'bg-gray-50'}`}>
                <div className="text-2xl mb-2">{bindings.ai ? '✅' : '⏸️'}</div>
                <div className="font-medium">AI Inference</div>
              </div>
            </div>
          ) : (
            <p className="text-gray-500">Loading bindings status...</p>
          )}
        </div>

        {/* Developer Hub CTA - PRIMARY */}
        <a
          href="#dev"
          className="block bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 rounded-lg shadow-2xl p-8 mb-6 text-white hover:shadow-3xl transition-all transform hover:scale-[1.02] border-2 border-blue-500"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-5xl">🛠️</span>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-2xl font-bold">Developer Hub</h3>
                  <span className="bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded">
                    NEW
                  </span>
                </div>
                <p className="text-gray-300 mb-3">
                  Self-documenting, self-auditing project dashboard - Test, Files, Links, Security
                </p>
                <div className="flex gap-3">
                  <span className="bg-white/10 backdrop-blur-sm rounded-full px-3 py-1 text-sm">
                    🧪 System Test
                  </span>
                  <span className="bg-white/10 backdrop-blur-sm rounded-full px-3 py-1 text-sm">
                    📁 File Map
                  </span>
                  <span className="bg-white/10 backdrop-blur-sm rounded-full px-3 py-1 text-sm">
                    🔗 Links
                  </span>
                  <span className="bg-white/10 backdrop-blur-sm rounded-full px-3 py-1 text-sm">
                    🔒 Security
                  </span>
                </div>
              </div>
            </div>
            <div className="text-5xl">→</div>
          </div>
        </a>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => setShowValidator(true)}
              className="p-4 border-2 border-blue-500 bg-blue-50 rounded-lg hover:bg-blue-100 transition text-left"
            >
              <div className="text-2xl mb-2">✅</div>
              <div className="font-medium text-blue-700">Test Resources</div>
              <div className="text-sm text-blue-600">Validate all bindings</div>
            </button>

            <button
              onClick={testAI}
              className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition text-left"
            >
              <div className="text-2xl mb-2">🤖</div>
              <div className="font-medium">Test AI</div>
              <div className="text-sm text-gray-600">Quick AI inference test</div>
            </button>

            <a
              href="#dev"
              className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition text-left"
            >
              <div className="text-2xl mb-2">🛠️</div>
              <div className="font-medium">Developer Hub</div>
              <div className="text-sm text-gray-600">Complete dev dashboard</div>
            </a>
          </div>
        </div>

        {/* Documentation Links */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <a
            href="#test"
            className="bg-white border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition"
          >
            <div className="text-2xl mb-2">🧪</div>
            <div className="font-medium text-gray-900">System Test</div>
            <div className="text-sm text-gray-600">Verify all integrations</div>
          </a>

          <a
            href="#files"
            className="bg-white border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition"
          >
            <div className="text-2xl mb-2">📁</div>
            <div className="font-medium text-gray-900">File Map</div>
            <div className="text-sm text-gray-600">Project structure</div>
          </a>

          <a
            href="#security"
            className="bg-white border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition"
          >
            <div className="text-2xl mb-2">🔒</div>
            <div className="font-medium text-gray-900">Security Audit</div>
            <div className="text-sm text-gray-600">Check for vulnerabilities</div>
          </a>
        </div>
      </main>
    </div>
  );
}
